import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Button,
  Page,
  PageHeader,
  Select,
  backLinkClasses,
  cardClasses,
} from '@evogard/ui';
import {
  DESK_IMPORT_DUE_DAYS,
  format as formatMoney,
  formatDate,
  formatInstant,
  fromColumn,
  importTargetDate,
} from '@evogard/domain';
import type { DeskAutoImport, DeskImportDetail, DeskImportSummary } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { DESK_KEY, useDeskSummaries, useInvalidateDesk } from './useDesk.js';

/**
 * A importação de inadimplentes — "Importação SGA" do legado (`App.tsx:1789`).
 *
 * Escolhe a base e o dia de vencimento, gera a prévia, confere item a item e
 * põe no quadro. A prévia sai do boleto da casa, sem chamada ao SGA, e não
 * entra duas vezes.
 *
 * Desde 22/10/2026 a base pode ligar a importação AUTOMÁTICA, que faz isto
 * sozinha no dia seguinte ao vencimento. A tela diz o que ela está fazendo
 * hoje — o mesmo estado com que o job decide — e marca as prévias dela. A
 * manual continua para o vencimento que a automática não importou e para
 * conferir.
 */
export function DeskImportScreen() {
  const { ready } = useDeskSummaries({});
  const allowed = ready.filter((s) => s.summary.can.import);
  const [slug, setSlug] = useState('');
  const tenant = slug || allowed[0]?.tenant.slug || '';
  const [day, setDay] = useState<number>(15);
  const [selected, setSelected] = useState<number | null>(null);
  const invalidate = useInvalidateDesk();

  const list = useQuery({
    enabled: tenant !== '',
    queryKey: [...DESK_KEY, 'importacoes', tenant],
    queryFn: async () =>
      ok(await api.billingDesk.imports({ params: { tenant } }), 200, 'Não foi possível carregar as prévias.'),
  });
  const detail = useQuery({
    enabled: tenant !== '' && selected !== null,
    queryKey: [...DESK_KEY, 'importacao', tenant, selected],
    queryFn: async () =>
      ok(
        await api.billingDesk.importDetail({ params: { tenant, id: selected! } }),
        200,
        'Não foi possível abrir a prévia.',
      ),
  });

  const act = useMutation({
    mutationFn: async (run: () => Promise<DeskImportDetail>) => run(),
    onSuccess: (d) => {
      setSelected(d.import.id);
      invalidate();
    },
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Importar inadimplentes"
        back={
          <Link to="/billing/desk" className={backLinkClasses()}>
            Mesa de cobrança
          </Link>
        }
      />

      {allowed.length === 0 && ready.length > 0 && (
        <Alert tone="informacao">Seu grupo não pode importar inadimplentes em nenhuma das suas bases.</Alert>
      )}

      {allowed.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          {allowed.length > 1 && (
            <div className="w-56">
              <Select label="Base" value={tenant} onChange={(e) => { setSlug(e.target.value); setSelected(null); }}>
                {allowed.map((s) => (
                  <option key={s.tenant.slug} value={s.tenant.slug}>
                    {s.tenant.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="w-56">
            <Select
              label="Vencimento (dia)"
              value={String(day)}
              onChange={(e) => setDay(Number(e.target.value))}
              help={`Busca o vencimento de ${formatDate(importTargetDate(day))}.`}
            >
              {DESK_IMPORT_DUE_DAYS.map((d) => (
                <option key={d} value={d}>
                  Dia {d}
                </option>
              ))}
            </Select>
          </div>
          <Button
            loading={act.isPending}
            onClick={() =>
              act.mutate(async () =>
                ok(
                  await api.billingDesk.generateImport({ params: { tenant }, body: { day } }),
                  201,
                  'Não foi possível gerar a prévia.',
                ),
              )
            }
          >
            Gerar prévia
          </Button>
        </div>
      )}
      <p className="text-sm text-texto-suave">
        Boletos em aberto e não pagos do dia — o vencimento de hoje ainda não entra; sai quem está fora da
        faixa de valor da configuração da Mesa, quem tem outro boleto em aberto com vencimento anterior, veículo que
        não é ATIVO nem INADIMPLENTE (ou é MIGRADO) e as cooperativas que a configuração exclui.
      </p>
      {list.data && <AutoImportStatus auto={list.data.auto} onOpen={setSelected} />}
      {act.error && <Alert tone="erro">{act.error.message}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <section aria-label="Prévias recentes" className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-texto-suave">Prévias recentes</h2>
          {list.data?.items.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => setSelected(i.id)}
              className={cardClasses({
                density: 'densa',
                interactive: true,
                tone: selected === i.id ? 'primaria' : 'neutro',
                className: 'text-left text-sm',
              })}
            >
              <span className="block font-medium">
                Vencimento {formatDate(i.dueDate)}
                {i.origin === 'automatica' && <span className="font-normal text-texto-suave"> · automática</span>}
              </span>
              <span className="block text-xs text-texto-suave">
                {i.eligibleCount} elegíveis · {summaryState(i)}
              </span>
            </button>
          ))}
          {list.data?.items.length === 0 && <p className="text-sm text-texto-suave">Nenhuma prévia ainda.</p>}
        </section>

        {detail.data && (
          <section aria-label="A prévia" className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-texto">
              Vencimento {formatDate(detail.data.import.dueDate)}
            </h2>
            <p className="text-sm text-texto-suave">
              Faixa de valor desta prévia: {rangeText(detail.data.import.minAmount, detail.data.import.maxAmount)}
            </p>
            <p className="text-sm text-texto-suave">
              Em aberto: {detail.data.import.openCount} · Fora da faixa de valor:{' '}
              {detail.data.import.discardedOutOfRange} · Com outro boleto em aberto de vencimento anterior:{' '}
              {detail.data.import.discardedEarlierOpen} · Sem veículo: {detail.data.import.discardedWithoutVehicle} ·
              Situação do veículo: {detail.data.import.discardedVehicleStatus} · Cooperativa:{' '}
              {detail.data.import.discardedCooperative} · Elegíveis: {detail.data.import.eligibleCount}
            </p>
            {heldAutomatic(detail.data.import) && (
              <Alert tone="alerta">
                A importação automática não pôs esta prévia no quadro: nenhum dos{' '}
                {detail.data.import.filteredCount} candidatos tem veículo no cadastro da casa. É o cadastro de
                veículos que falta, não a inadimplência — gere a prévia de novo quando ele chegar.
              </Alert>
            )}
            {detail.data.import.committedAt ? (
              <Alert tone="sucesso">
                No quadro desde {formatInstant(detail.data.import.committedAt)}
                {detail.data.import.committedBy === null && ', pela importação automática'}:{' '}
                {detail.data.import.committedInserted} novo(s), {detail.data.import.committedKept} já estavam.
              </Alert>
            ) : (
              <div>
                <Button
                  disabled={detail.data.items.length === 0}
                  loading={act.isPending}
                  onClick={() =>
                    window.confirm(`Pôr ${detail.data!.items.length} card(s) no quadro?`) &&
                    act.mutate(async () =>
                      ok(
                        await api.billingDesk.commitImport({
                          params: { tenant, id: detail.data!.import.id },
                          body: {},
                        }),
                        200,
                        'Não foi possível pôr no quadro.',
                      ),
                    )
                  }
                >
                  Pôr no quadro
                </Button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-texto-suave">
                    <th className="py-1 pr-3">Nome</th>
                    <th className="py-1 pr-3">Nosso nº</th>
                    <th className="py-1 pr-3">Valor</th>
                    <th className="py-1 pr-3">Placa</th>
                    <th className="py-1 pr-3">Situação</th>
                    <th className="py-1 pr-3">Consultor</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {detail.data.items.map((it) => {
                    const amount = it.amount === null ? null : fromColumn(it.amount);
                    return (
                      <tr key={it.id} className="border-t border-borda">
                        <td className="py-1 pr-3">
                          {it.name ?? '—'}
                          {it.alreadyOnDesk && <span className="block text-xs text-texto-suave">já está no quadro</span>}
                        </td>
                        <td className="py-1 pr-3">{it.nossoNumero ?? '—'}</td>
                        <td className="py-1 pr-3">{amount === null ? '—' : formatMoney(amount)}</td>
                        <td className="py-1 pr-3">{it.plate ?? '—'}</td>
                        <td className="py-1 pr-3">{it.vehicleStatus ?? '—'}</td>
                        <td className="py-1 pr-3">{it.consultant ?? '—'}</td>
                        <td className="py-1 text-right">
                          {!detail.data!.import.committedAt && (
                            <Button
                              size="sm"
                              variant="discreto"
                              onClick={() =>
                                act.mutate(async () =>
                                  ok(
                                    await api.billingDesk.removeImportItem({
                                      params: { tenant, id: detail.data!.import.id, itemId: it.id },
                                      body: {},
                                    }),
                                    200,
                                    'Não foi possível tirar o item.',
                                  ),
                                )
                              }
                            >
                              Tirar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {detail.data.items.length === 0 && <p className="text-sm text-texto-suave">Sem itens elegíveis.</p>}
          </section>
        )}
      </div>
    </Page>
  );
}

/**
 * O que a importação automática da base está fazendo hoje — o estado com que
 * o job decide, dito como a operação o lê. Cada frase afirma só o que o
 * estado garante: "esperando o ERP" diz desde quando, e não promete hora.
 */
function AutoImportStatus({ auto, onOpen }: { auto: DeskAutoImport; onOpen: (id: number) => void }) {
  const due = auto.dueDate === null ? null : formatDate(auto.dueDate);
  const open =
    auto.importId === null ? null : (
      <Button size="sm" variant="discreto" onClick={() => onOpen(auto.importId!)}>
        Abrir a prévia
      </Button>
    );
  switch (auto.state) {
    case 'desligada':
      return (
        <p className="text-sm text-texto-suave">
          Importação automática desligada nesta base — quem configura a Mesa a liga em Configurar a Mesa.
        </p>
      );
    case 'sem_vencimento':
      return (
        <Alert tone="informacao">Importação automática ligada. Ontem não foi dia de vencimento: nada a importar hoje.</Alert>
      );
    case 'antes_do_horario':
      return (
        <Alert tone="informacao">Importação automática ligada: o vencimento de {due} entra a partir das 7h.</Alert>
      );
    case 'nucleo_desatualizado':
      return (
        <Alert tone="alerta">
          A importação automática do vencimento de {due} está esperando o ERP:{' '}
          {auto.readAt === null
            ? 'os boletos desse vencimento ainda não foram lidos do ERP.'
            : `os boletos desse vencimento foram atualizados pela última vez em ${formatInstant(auto.readAt)}, e precisam ser relidos depois da meia-noite para não cobrar quem pagou ontem.`}{' '}
          Ela tenta de novo de hora em hora.
        </Alert>
      );
    case 'feita':
      return (
        <Alert tone="sucesso">
          O vencimento de {due} já foi importado. {open}
        </Alert>
      );
    case 'retida':
      return (
        <Alert tone="alerta">
          A importação automática do vencimento de {due} gerou a prévia e NÃO pôs os cards no quadro: nenhum
          candidato tem veículo no cadastro da casa. Ela não tenta de novo — confira a prévia, ou gere outra à mão
          quando o cadastro de veículos chegar. {open}
        </Alert>
      );
    case 'pronta':
      return (
        <Alert tone="informacao">
          Importação automática ligada: o vencimento de {due} entra na próxima passada, de hora em hora aos 25
          minutos.
        </Alert>
      );
  }
}

/** Onde a prévia está: no quadro, retida pela automática, ou esperando quem confere. */
function summaryState(i: DeskImportSummary): string {
  if (i.committedAt) return `no quadro desde ${formatInstant(i.committedAt)}`;
  if (heldAutomatic(i)) return 'retida: sem veículo no cadastro';
  return 'a conferir';
}

/** A automática que não entrou porque todo candidato caiu sem veículo — ver `runAutomatic`. */
function heldAutomatic(i: DeskImportSummary): boolean {
  return (
    i.origin === 'automatica' &&
    i.committedAt === null &&
    i.filteredCount > 0 &&
    i.eligibleCount === 0 &&
    i.discardedWithoutVehicle === i.filteredCount
  );
}

/** "R$ 150,00 a R$ 100.000,00" — a faixa com que a prévia foi gerada. */
function rangeText(min: string, max: string): string {
  const low = fromColumn(min);
  const high = fromColumn(max);
  if (low === null || high === null) return 'ilegível';
  return `${formatMoney(low)} a ${formatMoney(high)}`;
}
